import { I18nLocalesEnum } from '@zaoblako/shared/common/enums/i18n.locales.enum';
import type { EnumI18nInterface } from '@zaoblako/shared/common/interfaces/enum.i18n.interface';
import type { SerdeTransformFunctionInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-transform-function.interface';
import type { SerdeContextInterface } from '@zaoblako/shared/modules/serde/interfaces/serde-context.interface';

import * as yup from 'yup';

type ErrorMessageCallback = (context: SerdeContextInterface, value: any) => string;

function createErrorMessageCallback<E extends string | number = string | number>(enumeration: Record<string, string | number>, i18n?: EnumI18nInterface<E>): ErrorMessageCallback {
  return (context, value: any): string => {
    const locale = context.data.locale || I18nLocalesEnum.RU_RU;
      const availableValues = i18n?.[locale]
        ? Object
          .entries(i18n[locale])
          .map(([value, name]) => `"${value}" (${name})`)
          .join(', ')
        : Object
          .values(enumeration)
          .map(value => `"${value}"`)
          .join(', ')
      return `Недопустимое значение: "${value}". Допускаются только следующие значения: ${availableValues}.`;
  };
}

/**
 * Create validator for enumeration value
 *
 * @param enumeration Enumeration to validate
 * @param [i18n] Localized human-readable string representations of enumeration values.
 * Need for error message. If not defined, then enumaration values will be displayed as-is.
 *
 * @returns Validator callback for SerDe DTO instance
 */
export function commonEnumValidator<E extends string | number = string | number>(enumeration: Record<string, string | number>, i18n?: EnumI18nInterface<E>): SerdeTransformFunctionInterface<E> {
  const types = new Set(
    Object
      .values(enumeration)
      .map(value => typeof value),
  );
  const getErrorMessage = createErrorMessageCallback(enumeration, i18n);

  if (types.has('string') && types.has('number')) {
    return (context): yup.Schema<E> => yup
      .mixed()
      .transform(value => {
        const num = Number(value);
        return isNaN(num) ? value : num;
      })
      .oneOf(
        Object.values(enumeration),
        ({ value }) => getErrorMessage(context, value)
      ) as yup.Schema<any>;
  } else if (types.has('string')) {
    return (context): yup.Schema<E> => yup
      .string()
      .oneOf(
        Object.values(enumeration) as string[],
        ({ value }) => getErrorMessage(context, value)
      ) as yup.Schema<any>;
  } else if (types.has('number')) {
    return (context): yup.Schema<E> => yup
      .number()
      .oneOf(
        Object.values(enumeration) as number[],
        ({ value }) => getErrorMessage(context, value)
      ) as yup.Schema<any>;
  } else {
    throw new Error('Only enumeration of strings and numbers are supported!');
  }
}

/**
 * Create validator for enumeration values list
 *
 * @param enumeration Enumeration to validate
 * @param [i18n] Localized human-readable string representations of enumeration values.
 * Need for error message. If not defined, then enumaration values will be displayed as-is.
 *
 * @returns Validator callback for SerDe DTO instance
 */
commonEnumValidator.forArray = function commonEnumValidatorForArray<E extends string | number = string | number>(
  enumeration: Record<string, string | number>,
  i18n?: EnumI18nInterface<E>,
): SerdeTransformFunctionInterface<E[]> {
  const types = new Set(
    Object
      .values(enumeration)
      .map(value => typeof value),
  );
  const getErrorMessage = createErrorMessageCallback(enumeration, i18n);

  if (types.has('string') && types.has('number')) {
    return (context): yup.Schema<E[]> => yup.array(
      yup
        .mixed()
        .transform(value => {
          const num = Number(value);
          return isNaN(num) ? value : num;
        })
        .oneOf(
          Object.values(enumeration),
          ({ value }) => getErrorMessage(context, value)
        )
    ) as yup.Schema<any>;
  } else if (types.has('string')) {
    return (context): yup.Schema<E[]> => yup.array(
      yup
        .string()
        .oneOf(
          Object.values(enumeration) as string[],
          ({ value }) => getErrorMessage(context, value)
        ),
    ) as yup.Schema<any>;
  } else if (types.has('number')) {
    return (context): yup.Schema<E[]> => yup.array(
      yup
        .number()
        .oneOf(
          Object.values(enumeration) as number[],
          ({ value }) => getErrorMessage(context, value)
        ),
    ) as yup.Schema<any>;
  } else {
    throw new Error('Only enumeration of strings and numbers are supported!');
  }
}
